import { useCallback, useEffect, useState } from 'react';
import { showToast } from '../../services/toast.service';
import { getStorageCanisterByTank, getStorageSites, getStorageTanksBySite } from '../../api/storageItemsApi';
import {
  getAccountStorageCanisterByTank,
  getAccountStorageSites,
  getAccountStorageTanksBySite,
} from '../../api/accountStorageItemsApi';
import FilterDropdown, { FilterOption } from '../custom-input/FilterDropdown';
import { AnimatedDots } from '../loader/AnimatedDots';
import { selectOption } from '../../types/interfaces/selectOption.interface';
import { AccountStorageCanister, AccountStorageSite, AccountStorageTank, PagedResponse } from '../../types/interfaces';
import { StorageCanister, StorageSite, StorageTank } from '../../types/interfaces/storage.interfaces';
import { StorageTankOptions } from '../../types/enums/storageTank.enum';
import { toastMessages } from '../../constants/errorMessages';

interface SelectStorageItemsProps {
  accountId?: number;
  selectedSite: selectOption;
  selectedTank: selectOption;
  selectedCanister: selectOption;
  setSelectedSite: (e: FilterOption<any>) => void;
  setSelectedTank: (e: FilterOption<any>) => void;
  setSelectedCanister: (e: FilterOption<any>) => void;
  storageTankOptions?: StorageTankOptions;
  activeLocationsOnly?: boolean;
  disabled?: boolean;
}

const normalizeList = <T,>(data: T[] | PagedResponse<T>): T[] => {
  if (Array.isArray(data)) return data;
  return data?.result ?? [];
};

const SelectStorageItems = ({
  accountId,
  selectedSite,
  selectedTank,
  selectedCanister,
  setSelectedSite,
  setSelectedTank,
  setSelectedCanister,
  storageTankOptions = StorageTankOptions.Stationary,
  activeLocationsOnly = false,
  disabled = false,
}: SelectStorageItemsProps) => {
  const initialState = { value: '', name: '' };
  const [storageSites, setStorageSites] = useState<selectOption[]>([initialState]);
  const [storageTanks, setStorageTanks] = useState<selectOption[]>([initialState]);
  const [storageCanisters, setStorageCanisters] = useState<selectOption[]>([initialState]);
  const [isLoading, setIsLoading] = useState({
    sites: true,
    tanks: false,
    canisters: false,
  });

  const useAccountStorage = !!accountId;

  const getSites = useCallback(async () => {
    try {
      if (useAccountStorage) {
        const response = await getAccountStorageSites({
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
          sort: 'Name',
        });
        const sites = normalizeList(response.data as AccountStorageSite[] | PagedResponse<AccountStorageSite>).map(
          (s: AccountStorageSite) => ({
            value: String(s.accountStorageSiteId),
            name: s.name + (s.active ? '' : ' - (Inactive)'),
          }),
        );
        setStorageSites(sites);
      } else {
        const response = await getStorageSites({
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
          sort: 'Name',
        });
        const sites = (response.data as StorageSite[]).map((s: StorageSite) => ({
          value: String(s.storageSiteId),
          name: s.name + (s.active ? '' : ' - (Inactive)'),
        }));
        setStorageSites(sites);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(prevIsLoading => ({ ...prevIsLoading, sites: false }));
    }
  }, [activeLocationsOnly, useAccountStorage]);

  useEffect(() => {
    getSites();
  }, [getSites]);

  const getTanks = async (siteId: string) => {
    setIsLoading(prevIsLoading => ({ ...prevIsLoading, tanks: true }));

    try {
      if (useAccountStorage) {
        const response = await getAccountStorageTanksBySite(siteId, storageTankOptions, {
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
          sort: 'Name',
        });
        const tanks = normalizeList(response.data as AccountStorageTank[] | PagedResponse<AccountStorageTank>).map(
          (s: AccountStorageTank) => ({
            value: String(s.accountStorageTankId),
            name: s.name + (s.active ? '' : ' - (Inactive)'),
          }),
        );
        setStorageTanks(tanks);
      } else {
        const response = await getStorageTanksBySite(siteId, storageTankOptions, {
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
          sort: 'Name',
        });
        const tanks = response.data.map((s: StorageTank) => ({
          value: String(s.storageTankId),
          name: s.name + (s.active ? '' : ' - (Inactive)'),
        }));
        setStorageTanks(tanks);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(prevIsLoading => ({ ...prevIsLoading, tanks: false }));
    }
  };

  const getCanisters = async (tankId: string) => {
    setIsLoading(prevIsLoading => ({ ...prevIsLoading, canisters: true }));

    try {
      if (useAccountStorage) {
        const response = await getAccountStorageCanisterByTank(tankId, {
          include: 'OnFarmSpecimenLocations',
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
          sort: 'Name',
        });
        const canisters = normalizeList(response.data as AccountStorageCanister[] | PagedResponse<AccountStorageCanister>).map(
          (s: AccountStorageCanister) => ({
            value: String(s.accountStorageCanisterId),
            name:
              s.name +
              (s.active ? '' : ' - (Inactive)') +
              (s.accountCanisterInventoryCount ? '\t(' + s.accountCanisterInventoryCount + ')' : ''),
          }),
        );
        setStorageCanisters(canisters);
      } else {
        const response = await getStorageCanisterByTank(tankId, {
          include: 'SpecimenLocations',
          filter: activeLocationsOnly ? 'Active eq true' : undefined,
          sort: 'Name',
        });
        const canisters = (response.data as StorageCanister[]).map((s: StorageCanister) => ({
          value: String(s.storageCanisterId),
          name:
            s.name + (s.active ? '' : ' - (Inactive)') + (s.canisterInventoryCount ? '\t(' + s.canisterInventoryCount + ')' : ''),
        }));
        setStorageCanisters(canisters);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(prevIsLoading => ({ ...prevIsLoading, canisters: false }));
    }
  };

  const isLoadingPlaceHolder = (isLoadingOptions: boolean, filterDropdownName: string) => {
    return isLoadingOptions ? (
      <span>
        Loading {filterDropdownName}&nbsp;
        <AnimatedDots />
      </span>
    ) : (
      `Select ${filterDropdownName}`
    );
  };

  useEffect(() => {
    if (selectedSite.value.length > 0) {
      getTanks(selectedSite.value);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedTank.value.length > 0) {
      getCanisters(selectedTank.value);
    }
  }, [selectedTank]);

  return (
    <div className="select-storage-items">
      <div className="form-row">
        <label>Site: </label>
        <div className="input-container">
          <FilterDropdown
            placeholder={isLoadingPlaceHolder(isLoading.sites, 'Site')}
            options={storageSites}
            validate={false}
            value={selectedSite}
            onChange={e => {
              setSelectedSite(e);
              setSelectedTank(initialState);
              setSelectedCanister(initialState);
            }}
            disabled={disabled || (isLoading.sites && storageSites[0] === initialState)}
          />
        </div>
      </div>
      <div className="form-row">
        <label>Tank: </label>
        <div className="input-container">
          <FilterDropdown
            placeholder={isLoadingPlaceHolder(isLoading.tanks, 'Tank')}
            options={storageTanks}
            validate={false}
            value={selectedTank}
            onChange={e => {
              setSelectedTank(e);
              setSelectedCanister(initialState);
            }}
            disabled={disabled || !selectedSite.value || isLoading.tanks}
          />
        </div>
      </div>
      <div className="form-row">
        <label>Canister: </label>
        <div className="input-container">
          <FilterDropdown
            placeholder={isLoadingPlaceHolder(isLoading.canisters, 'Canister')}
            options={storageCanisters}
            validate={false}
            value={selectedCanister}
            onChange={e => {
              setSelectedCanister(e);
            }}
            disabled={disabled || !selectedTank.value || !selectedSite.value || isLoading.canisters}
          />
        </div>
      </div>
    </div>
  );
};

export default SelectStorageItems;
